import { Node } from "@xyflow/react";
import { SequenceNodeData } from "../SequenceNode";

interface SequenceSelectionColorToolbarProps {
  selectedNodes: Node<SequenceNodeData>[];
  selectedCommentZoneIds?: string[];
  selectedTextboxIds?: string[];
  onSetSelectionColor: (
    nodeIds: string[],
    commentZoneIds: string[],
    textboxIds: string[],
    color: string | null,
  ) => void;
}

const selectionColorPresets = [
  "#3AC4FF",
  "#8FBC8F",
  "#D35F5F",
  "#E8A735",
  "#9B59B6",
  "#1ABC9C",
];

export default function SequenceSelectionColorToolbar({
  selectedNodes,
  selectedCommentZoneIds = [],
  selectedTextboxIds = [],
  onSetSelectionColor,
}: SequenceSelectionColorToolbarProps) {
  const nodeIds = selectedNodes.map((n) => n.id);
  const commentZoneIds = selectedCommentZoneIds;
  const textboxIds = selectedTextboxIds;

  const handlePointerDown = (e: React.PointerEvent) => {
    // Stop React Flow from processing this as a canvas interaction
    e.stopPropagation();
  };

  const handlePresetClick = (hex: string) => {
    onSetSelectionColor(nodeIds, commentZoneIds, textboxIds, hex);
  };

  const handleClearClick = () => {
    onSetSelectionColor(nodeIds, commentZoneIds, textboxIds, null);
  };

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSetSelectionColor(nodeIds, commentZoneIds, textboxIds, e.target.value);
  };

  return (
    <div
      className="absolute bottom-3 left-1/2 -translate-x-1/2 z-[1000] flex gap-1.5 items-center px-2 py-1.5 rounded-lg bg-creator-surface shadow-md border border-creator-border backdrop-blur-sm"
      data-testid="sequence-selection-color-toolbar"
      onPointerDown={handlePointerDown}
    >
      {selectionColorPresets.map((hex) => (
        <button
          key={hex}
          type="button"
          className="w-5 h-5 rounded-full border border-creator-border hover:border-creator-primary focus:outline-none focus:ring-1 focus:ring-creator-primary shrink-0"
          style={{ backgroundColor: hex }}
          onPointerDown={handlePointerDown}
          onClick={() => handlePresetClick(hex)}
          title={hex}
          aria-label={`Set color to ${hex}`}
        />
      ))}
      <span className="w-px h-4 bg-creator-border shrink-0" aria-hidden />
      <label
        className="cursor-pointer shrink-0"
        onPointerDown={handlePointerDown}
      >
        <input
          type="color"
          className="block w-5 h-5 bg-transparent rounded-full border cursor-pointer border-creator-border"
          onChange={handleColorChange}
          onPointerDown={handlePointerDown}
          title="Custom color"
        />
      </label>
      <span className="w-px h-4 bg-creator-border shrink-0" aria-hidden />
      <button
        type="button"
        className="text-[11px] font-medium text-creator-primary hover:text-creator-primary-hover focus:outline-none px-1 py-0.5 rounded"
        onPointerDown={handlePointerDown}
        onClick={handleClearClick}
      >
        Clear
      </button>
    </div>
  );
}
